/**
 * Auswahl mit Suche (`Combobox`, Baustein B, Ticket 1.257; DESIGN „Auswahl mit Suche“, EXPERIENCE §4.3) als reine
 * Logik ohne DOM: Text falten, Optionen filtern und markieren, Tastatur. Der Baustein `components/ui/combobox.tsx` setzt
 * nur noch zusammen; Vitest prüft hier jede Regel.
 */

/** Ein Eintrag der Auswahl: gespeichert wird `value` (ISO-Kürzel, id), angezeigt `label`. */
export interface ComboOption {
  /** Technischer Wert (ISO-Kürzel, id). */
  value: string;
  /** Angezeigter Name, z. B. „Österreich · AT“ oder „CHF – Schweizer Franken“. */
  label: string;
  /** Weitere Suchbegriffe (Kürzel, Nummer, Name), die nicht oder anders im `label` stehen. */
  keys?: readonly string[];
  /** Kleine zweite Zeile (z. B. Bestand eines Artikels); wird nicht durchsucht. */
  hint?: string | null;
}

/** Treffer: die Option und die halbfetten Stellen im `label` (Anfang inklusive, Ende exklusive, UTF-16). */
export interface ComboMatch {
  option: ComboOption;
  ranges: readonly (readonly [number, number])[];
}

/** Ergebnis der Filterung: häufige Werte oben (über dem feinen Strich), darunter der Rest. */
export interface ComboFilterResult {
  frequent: ComboMatch[];
  rest: ComboMatch[];
}

const UMLAUTS: Readonly<Record<string, string>> = { ä: 'a', ö: 'o', ü: 'u' };

/**
 * Gefalteter Text mit Herkunft je Zeichen: `start[i]`/`end[i]` = Bereich im Original, aus dem Zeichen `i` stammt.
 * Regeln: Groß/klein egal; ä = ae = a, ö = oe = o, ü = ue = u; ß = ss; andere Akzente weg (é = e). `digraphs = false`:
 * „ae/oe/ue“ bleiben stehen (sonst fände „el“ nicht „Israel“); gesucht wird in beiden Fassungen.
 */
export function foldWithMap(text: string, digraphs = true): { folded: string; start: number[]; end: number[] } {
  let folded = '';
  const start: number[] = [];
  const end: number[] = [];
  const push = (s: string, from: number, to: number) => {
    for (const ch of s) {
      folded += ch;
      start.push(from);
      end.push(to);
    }
  };
  for (let i = 0; i < text.length; i++) {
    const lower = text[i]!.toLowerCase();
    const umlaut = UMLAUTS[lower];
    if (umlaut) push(umlaut, i, i + 1);
    else if (lower === 'ß') push('ss', i, i + 1);
    else if (digraphs && (lower === 'a' || lower === 'o' || lower === 'u') && text[i + 1]?.toLowerCase() === 'e') {
      push(lower, i, i + 2);
      i++;
    } else {
      const base = lower.normalize('NFD').replace(/\p{M}/gu, '');
      push(base === '' ? lower : base, i, i + 1);
    }
  }
  return { folded, start, end };
}

/** Text für den Vergleich falten (siehe `foldWithMap`). */
export function foldText(text: string, digraphs = true): string {
  return foldWithMap(text, digraphs).folded;
}

/** Ein Suchbegriff in beiden Faltungen: mit „ae = a“ und ohne. */
export interface QueryTerm {
  full: string;
  plain: string;
}

/** Suchbegriffe: an Leerzeichen getrennt, gefaltet, ohne leere. */
export function queryTerms(query: string): QueryTerm[] {
  return query
    .trim()
    .split(/\s+/)
    .map((t) => ({ full: foldText(t), plain: foldText(t, false) }))
    .filter((t) => t.full !== '');
}

/** Trifft der Begriff den Text (in einer der beiden Faltungen)? */
function termHits(text: string, term: QueryTerm): boolean {
  return foldText(text).includes(term.full) || foldText(text, false).includes(term.plain);
}

/** Halbfette Stellen im `label`: jedes Vorkommen jedes Begriffs, auf das Original zurückgerechnet, zusammengefasst. */
export function highlightRanges(label: string, terms: readonly QueryTerm[]): [number, number][] {
  const raw: [number, number][] = [];
  for (const digraphs of [true, false]) {
    const { folded, start, end } = foldWithMap(label, digraphs);
    for (const t of terms) {
      const term = digraphs ? t.full : t.plain;
      if (term === '') continue;
      let at = folded.indexOf(term);
      while (at >= 0) {
        raw.push([start[at]!, end[at + term.length - 1]!]);
        at = folded.indexOf(term, at + 1);
      }
    }
  }
  raw.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: [number, number][] = [];
  for (const r of raw) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

/** `label` in Stücke zerlegt: `hit` = halbfett. */
export function labelSegments(label: string, ranges: readonly (readonly [number, number])[]): { text: string; hit: boolean }[] {
  const out: { text: string; hit: boolean }[] = [];
  let at = 0;
  for (const [from, to] of ranges) {
    if (from > at) out.push({ text: label.slice(at, from), hit: false });
    out.push({ text: label.slice(from, to), hit: true });
    at = to;
  }
  if (at < label.length) out.push({ text: label.slice(at), hit: false });
  return out;
}

/**
 * Optionen filtern: jeder Begriff muss im Namen **oder** in einem Kürzel/einer Nummer (`keys`) stehen (gefaltet). Häufige
 * Werte (`frequent`, in dieser Reihenfolge) stehen oben und erscheinen unten nicht noch einmal; in beiden Teilen steht ein
 * genau passendes Kürzel vorn („ch“ → Schweiz vor Deutschland), sonst bleibt die Reihenfolge. `serverFiltered`: die Optionen kommen schon gefiltert
 * vom Server (z. B. Treffer über die EAN) – dann nur markieren, nichts weglassen.
 */
export function filterOptions(
  options: readonly ComboOption[],
  query: string,
  frequent: readonly string[] = [],
  serverFiltered = false,
): ComboFilterResult {
  const terms = queryTerms(query);
  const whole = foldText(query.trim());
  const matches: ComboMatch[] = [];
  for (const option of options) {
    if (!serverFiltered && terms.length > 0) {
      const hay = [option.label, ...(option.keys ?? [])].join('\u0000');
      if (!terms.every((t) => termHits(hay, t))) continue;
    }
    matches.push({ option, ranges: highlightRanges(option.label, terms) });
  }
  const exact = (m: ComboMatch) => whole !== '' && (m.option.keys ?? []).some((k) => foldText(k) === whole);
  const exactFirst = (list: ComboMatch[]) => [...list.filter(exact), ...list.filter((m) => !exact(m))];
  const top = frequent.flatMap((value) => matches.filter((m) => m.option.value === value).slice(0, 1));
  const topValues = new Set(top.map((m) => m.option.value));
  return { frequent: exactFirst(top), rest: exactFirst(matches.filter((m) => !topValues.has(m.option.value))) };
}

/** Option, deren Name genau dem Suchtext entspricht (gefaltet), z. B. vorhandener Hersteller. */
export function exactMatch(options: readonly ComboOption[], query: string): ComboOption | null {
  const norm = (text: string) => foldText(text.trim().replace(/\s+/g, ' '));
  const whole = norm(query);
  if (whole === '') return null;
  return options.find((o) => norm(o.label) === whole) ?? null;
}

/** „+ „Muster GmbH“ neu anlegen“ anbieten? Nur mit Text und ohne genau passenden Namen. */
export function offerCreate(options: readonly ComboOption[], query: string): boolean {
  return query.trim() !== '' && exactMatch(options, query) === null;
}

/** Startmarkierung beim Öffnen: der aktuelle Wert, sonst der erste Eintrag; leer → keine. */
export function initialActive(values: readonly string[], current: string | null): number {
  if (values.length === 0) return -1;
  const i = current === null ? -1 : values.indexOf(current);
  return i >= 0 ? i : 0;
}

export type ComboAction = 'none' | 'pick' | 'close';

export interface ComboKeyResult {
  /** Neue Markierung (−1 = keine). */
  active: number;
  /** `pick`: markierten Eintrag übernehmen und schließen; `close`: schließen ohne Änderung. */
  action: ComboAction;
  /** Fokus danach: zurück aufs Feld oder (Tab) weiter zum nächsten Element. */
  focus: 'field' | 'next' | null;
  /** Taste gehört dem Baustein (Standardverhalten des Browsers unterdrücken). */
  handled: boolean;
}

/**
 * Tastatur in der offenen Liste (WAI-ARIA Combobox): ↑/↓ markieren (nicht umlaufend), Pos1/Ende erster/letzter Eintrag,
 * Enter übernimmt und schließt (ohne Markierung: nichts), Tab übernimmt den markierten Eintrag und geht weiter, Esc
 * schließt ohne Änderung; der Fokus kehrt aufs Feld zurück.
 */
export function comboKey(state: { active: number; count: number }, key: string): ComboKeyResult {
  const { count } = state;
  const active = count === 0 ? -1 : Math.min(Math.max(state.active, -1), count - 1);
  const stay = (next: number): ComboKeyResult => ({ active: next, action: 'none', focus: null, handled: true });
  switch (key) {
    case 'ArrowDown':
      return stay(count === 0 ? -1 : Math.min(active + 1, count - 1));
    case 'ArrowUp':
      return stay(count === 0 ? -1 : Math.max(active - 1, 0));
    case 'Home':
      return stay(count === 0 ? -1 : 0);
    case 'End':
      return stay(count - 1);
    case 'Enter':
      return active >= 0 ? { active, action: 'pick', focus: 'field', handled: true } : stay(active);
    case 'Tab':
      return { active, action: active >= 0 ? 'pick' : 'close', focus: 'next', handled: true };
    case 'Escape':
      return { active, action: 'close', focus: 'field', handled: true };
    default:
      return { active, action: 'none', focus: null, handled: false };
  }
}

/**
 * Angebot zum Neuanlegen (Ticket 5.90, Entwurf Variante A): ohne Text oder bei genau passendem Namen nichts; ähnelt der
 * Text einem vorhandenen Eintrag (`similarOf`, z. B. `similarOption` des Besitzers), erst die Frage „Meinst du …?“ mit
 * dem vorhandenen Eintrag, sonst die Zeile „neu anlegen“.
 */
export type CreateOffer = { kind: 'none' } | { kind: 'create'; text: string } | { kind: 'similar'; text: string; match: ComboOption };

export function createOffer(options: readonly ComboOption[], query: string, similarOf?: (text: string) => ComboOption | null): CreateOffer {
  const text = query.trim().replace(/\s+/g, ' ');
  if (text === '' || exactMatch(options, text) !== null) return { kind: 'none' };
  const match = similarOf?.(text) ?? null;
  return match ? { kind: 'similar', text, match } : { kind: 'create', text };
}

/** Treffer mit der Frage „Meinst du …?“: der vorhandene Eintrag steht oben (Enter nimmt ihn), sonst wie gefiltert. */
export function withSimilarFirst(matches: readonly ComboMatch[], offer: CreateOffer): ComboMatch[] {
  if (offer.kind !== 'similar') return [...matches];
  const value = offer.match.value;
  const own = matches.find((m) => m.option.value === value) ?? { option: offer.match, ranges: [] };
  return [own, ...matches.filter((m) => m.option.value !== value)];
}
